@if (_loading()) {
  <div class="loading-container">
    <c-spinner color="primary"></c-spinner>
  </div>
} @else {
  <!-- Step 1: Select Method and Enter Source -->
  @if (currentStep() === 'select') {
    <div class="mfa-step">
      @if (showHeader) {
        <p class="mfa-description">{{ 'MFA_CONFIG_DESCRIPTION' | translate }}</p>
      }

      <div class="form-section">
        <c-row class="mb-3">
          <c-col md="6">
            <label cLabel for="mfaMethod" class="form-label-custom">{{ 'MFA_SELECT_METHOD' | translate }}</label>
            <select
              cSelect
              id="mfaMethod"
              [ngModel]="selectedMethod()"
              (ngModelChange)="selectedMethod.set($event); onMethodChange()"
            >
              @for (method of config.availableMethods; track method) {
                <option [ngValue]="method">{{ getMethodLabel(method) }}</option>
              }
            </select>
          </c-col>
        </c-row>

        @if (requiresSourceInput) {
          <c-row>
            <c-col md="6">
              <label cLabel for="verificationSource" class="form-label-custom">
                @if (selectedMethod() === 'phone') {
                  {{ 'MFA_PHONE_LABEL' | translate }}
                } @else {
                  {{ 'MFA_EMAIL_LABEL' | translate }}
                }
              </label>
              <span class="input-icon icon-right">
                <input
                  cFormControl
                  id="verificationSource"
                  type="text"
                  [placeholder]="getPlaceholder()"
                  [ngModel]="verificationSource()"
                  (ngModelChange)="verificationSource.set($event)"
                  [class.is-invalid]="hasInvalidInput()"
                />
                @if (!hasInvalidInput()) {
                  @if (selectedMethod() === 'phone') {
                    <i class="fas fa-mobile-notch input-icon-addon primary"></i>
                  } @else {
                    <i class="fas fa-envelope input-icon-addon primary"></i>
                  }
                }
              </span>
            </c-col>
          </c-row>
        }
      </div>

      <!-- Buttons for select step -->
      <div class="mfa-buttons">
        @if (showCancel) {
          <button cButton color="secondary" type="button" (click)="onCancel()">
            {{ 'MFA_CANCEL' | translate }}
          </button>
        }
        <button
          cButton
          color="primary"
          type="button"
          [disabled]="_loading() || !isValidInput()"
          (click)="onSendCode()"
        >
          {{ 'MFA_SEND_CODE' | translate }}
        </button>
      </div>
    </div>
  }

  <!-- Step 2: Enter Verification Code -->
  @if (currentStep() === 'verify') {
    <div class="mfa-step">
      <!-- 1. Description header -->
      @if (showHeader) {
        <p class="mfa-settings-header">{{ 'AUTH_MFA_MODAL_SETTINGS_HEADER' | translate }}</p>
        <hr class="mfa-separator" />
      }

      <!-- 2. Countdown progress bar with expiration label on same row -->
      @if (remainingTime() > 0) {
        <div class="timer-row">
          <div class="progress-wrapper">
            <c-progress [value]="timerProgress()" color="primary" [animated]="true"></c-progress>
          </div>
          <div class="expiration-label">
            {{ 'MFA_CODE_EXPIRATION' | translate }} <span class="time-value">{{ getExpirationMinutes() }}</span>
            {{ 'MFA_MINUTES' | translate }}
          </div>
        </div>
        <hr class="mfa-separator" />
      }

      <!-- 3. Method selection (optional, for regular MFA with pre-configured addresses) -->
      @if (showMethodInVerifyStep && config.availableMethods.length > 1) {
        <div class="form-section mb-3">
          <c-row>
            <c-col md="6">
              <label cLabel for="mfaMethodVerify" class="form-label-custom">{{
                'MFA_SELECT_METHOD' | translate
              }}</label>
              <select
                cSelect
                id="mfaMethodVerify"
                [ngModel]="selectedMethod()"
                (ngModelChange)="onMethodChangeInVerifyStep($event)"
              >
                @for (method of config.availableMethods; track method) {
                  <option [ngValue]="method">{{ getMethodLabel(method) }}</option>
                }
              </select>
            </c-col>
          </c-row>
        </div>
      }

      <!-- 4. Code sent message -->
      <p class="mfa-description code-sent">
        @if (codeSentMessage) {
          {{ codeSentMessage }}<i class="fas fa-check-circle"></i>
        } @else if (selectedMethod() === 'phone') {
          {{ 'MFA_CODE_SENT_SMS' | translate }}<i class="fas fa-check-circle"></i>
        } @else {
          {{ 'MFA_CODE_SENT_EMAIL' | translate }}<i class="fas fa-check-circle"></i>
        }
      </p>

      <!-- 5. Enter code input with resend link -->
      <div class="form-section">
        <c-row>
          <c-col md="6">
            <label cLabel for="verificationCode" class="form-label-custom">{{ 'MFA_ENTER_CODE' | translate }}</label>
            <div class="code-input-row">
              <input
                cFormControl
                id="verificationCode"
                type="text"
                maxlength="6"
                [placeholder]="'MFA_CODE_PLACEHOLDER' | translate"
                [ngModel]="verificationCode()"
                (ngModelChange)="verificationCode.set($event)"
              />
              <span class="resend-link" [class.disabled]="_loading() || !canResend()" (click)="onResendCode()">
                {{ 'MFA_RESEND' | translate }}
              </span>
            </div>
          </c-col>
        </c-row>
      </div>

      <!-- Buttons for verify step -->
      <div class="mfa-buttons">
        @if (showBack) {
          <button cButton color="primary" type="button" (click)="onBack()">
            <i class="fal fa-arrow-left me-1"></i>
            {{ 'MFA_BACK' | translate }}
          </button>
        }
        <button
          cButton
          color="primary"
          type="button"
          [disabled]="_loading() || !verificationCode()"
          (click)="onVerifyCode()"
        >
          {{ 'MFA_VERIFY' | translate }}
        </button>
      </div>
    </div>
  }
}
